import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { enhanceDataUrl, enhanceReceiptImage, fileToDataUrl, autoDetectAndCrop, detectReceiptBoundsBox, cropImageWithBounds, removeBackground, pdfToDataUrl } from "../lib/utils-image";
import { toast } from "sonner";
import { Camera, ImagePlus, Loader2, Pen, X, FileUp, Crop, Sparkles, Undo2, ZoomIn, ZoomOut, Maximize2, SplitSquareHorizontal, Check, Eraser } from "lucide-react";
import CropModal from "../components/CropModal";
import BeforeAfterSlider from "../components/BeforeAfterSlider";
import ReceiptDetectionOverlay from "../components/ReceiptDetectionOverlay";

const MIN_ZOOM = 1;
const MAX_ZOOM = 4;
const ZOOM_STEP = 0.25;

export default function AddInvoicePage() {
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const cameraRef = useRef(null);
  const [originalPreview, setOriginalPreview] = useState(null); // truly the original capture
  const [preview, setPreview] = useState(null);                  // current displayed image (may be auto-cropped)
  const [enhanced, setEnhanced] = useState(null);                // enhanced image for OCR
  const [analyzing, setAnalyzing] = useState(false);
  const [autoCropping, setAutoCropping] = useState(false);
  const [showCrop, setShowCrop] = useState(false);
  const [autoCropped, setAutoCropped] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [compareMode, setCompareMode] = useState(false);

  // Detection overlay state
  const [detectedBounds, setDetectedBounds] = useState(null);   // {x,y,width,height,imageWidth,imageHeight}
  const [overlayMode, setOverlayMode] = useState(false);        // showing original + green box, awaiting user confirmation

  // Capture mode is always single (batch mode removed per user request)

  // Background removal
  const [bgRemoved, setBgRemoved] = useState(false);
  const [bgRemoving, setBgRemoving] = useState(false);
  const [enhancedNoBg, setEnhancedNoBg] = useState(null); // cached version with bg removed
  const [previewWithBg, setPreviewWithBg] = useState(null); // cache of pre-bg-removal preview for toggling off

  // Touch state for pinch zoom
  const pinchStateRef = useRef({ startDistance: 0, startZoom: 1, active: false });
  const lastTapRef = useRef(0);

  const handleFile = async (file) => {
    if (!file) return;
    const isPdf = file.type === "application/pdf" || /\.pdf$/i.test(file.name || "");
    if (!isPdf && !file.type.startsWith("image/")) {
      toast.error("الرجاء اختيار صورة أو ملف PDF");
      return;
    }
    setAutoCropping(true);
    try {
      let original;
      if (isPdf) {
        toast.info("جاري تحويل ملف PDF إلى صورة…");
        original = await pdfToDataUrl(file);
      } else {
        original = await fileToDataUrl(file);
      }
      setOriginalPreview(original);
      setPreview(original);
      setAutoCropped(false);
      setZoom(1);

      // Detect bounds (only — don't crop yet, so user can see the overlay)
      const bounds = await detectReceiptBoundsBox(original).catch(() => null);
      setDetectedBounds(bounds);

      // Pre-compute an enhanced version (using detected bounds if any) for OCR
      const baseDataUrl = bounds ? await cropImageWithBounds(original, bounds) : original;
      const enhancedB64 = await enhanceDataUrl(baseDataUrl);
      setEnhanced(enhancedB64);

      if (bounds) {
        setOverlayMode(true); // show overlay → user confirms
        toast.success("تم اكتشاف حدود الفاتورة", {
          description: "اضغط 'تأكيد القص' للمتابعة أو 'استخدام الأصل' لإلغاء.",
        });
      } else {
        setOverlayMode(false);
        toast.info("لم نتمكن من اكتشاف حدود واضحة، يمكنك المتابعة أو الاقتصاص يدوياً.");
      }
    } catch (e) {
      console.warn("File handling failed:", e);
      toast.error("تعذر معالجة الصورة");
    } finally {
      setAutoCropping(false);
    }
  };

  // Confirm auto-detected bounds → apply crop and exit overlay mode
  const confirmDetection = async () => {
    if (!detectedBounds || !originalPreview) return;
    try {
      const cropped = await cropImageWithBounds(originalPreview, detectedBounds);
      setPreview(cropped);
      setAutoCropped(true);
      setOverlayMode(false);
      setBgRemoved(false);
      setEnhancedNoBg(null);
      setPreviewWithBg(null);
      const enhancedB64 = await enhanceDataUrl(cropped);
      setEnhanced(enhancedB64);
      toast.success("تم القص");
    } catch {
      toast.error("تعذر تطبيق القص");
    }
  };

  // Skip auto-crop and use original
  const skipDetection = async () => {
    if (!originalPreview) return;
    setOverlayMode(false);
    setPreview(originalPreview);
    setAutoCropped(false);
    setBgRemoved(false);
    setEnhancedNoBg(null);
    setPreviewWithBg(null);
    try {
      const enhancedB64 = await enhanceDataUrl(originalPreview);
      setEnhanced(enhancedB64);
      toast.success("سيتم استخدام الصورة الأصلية كما هي");
    } catch {
      setEnhanced(originalPreview);
    }
  };

  const undoAutoCrop = async () => {
    if (!originalPreview) return;
    setPreview(originalPreview);
    setAutoCropped(false);
    setZoom(1);
    try {
      const enhancedB64 = await enhanceDataUrl(originalPreview);
      setEnhanced(enhancedB64);
      toast.success("تمت العودة إلى الصورة الأصلية");
    } catch {
      setEnhanced(originalPreview);
    }
  };

  const applyCrop = async (croppedDataUrl) => {
    setShowCrop(false);
    setPreview(croppedDataUrl);
    setAutoCropped(false);
    setZoom(1);
    try {
      const enhancedB64 = await enhanceDataUrl(croppedDataUrl);
      setEnhanced(enhancedB64);
      toast.success("تم اقتصاص الصورة وتحسينها");
    } catch {
      setEnhanced(croppedDataUrl);
    }
  };

  const analyze = async () => {
    if (!enhanced) {
      toast.error("الرجاء اختيار صورة الفاتورة أولاً");
      return;
    }
    setAnalyzing(true);
    try {
      const res = await api.post("/ocr/analyze", { image_base64: enhanced });
      const data = res.data?.data || {};
      const success = res.data?.success;
      navigate("/review", {
        state: {
          extracted: data,
          imageBase64: enhanced,
          previewUrl: preview,
          ocrSuccess: success,
        },
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "تعذر تحليل الفاتورة");
    } finally {
      setAnalyzing(false);
    }
  };

  const manualEntry = () => {
    navigate("/review", {
      state: {
        extracted: {
          store_name: "",
          invoice_date: new Date().toISOString().slice(0, 10),
          invoice_time: "",
          invoice_number: "",
          total_amount: 0,
          tax_amount: 0,
          discount_amount: 0,
          payment_method: "",
          notes: "",
        },
        imageBase64: enhanced || "",
        previewUrl: preview,
        manual: true,
      },
    });
  };

  const reset = () => {
    setOriginalPreview(null);
    setPreview(null);
    setEnhanced(null);
    setAutoCropped(false);
    setOverlayMode(false);
    setDetectedBounds(null);
    setZoom(1);
    setCompareMode(false);
    setBgRemoved(false);
    setBgRemoving(false);
    setEnhancedNoBg(null);
    setPreviewWithBg(null);
    if (fileRef.current) fileRef.current.value = "";
    if (cameraRef.current) cameraRef.current.value = "";
  };

  // Toggle background removal on the current preview/enhanced image.
  const toggleRemoveBackground = async () => {
    if (!preview || !enhanced || bgRemoving) return;

    // Turning OFF — restore previous state
    if (bgRemoved) {
      if (previewWithBg) setPreview(previewWithBg);
      setBgRemoved(false);
      toast.success("تمت إعادة الخلفية الأصلية");
      return;
    }

    // Turning ON — compute (or reuse cached) background-removed version
    setBgRemoving(true);
    try {
      if (enhancedNoBg) {
        setPreviewWithBg(preview);
        setPreview(enhancedNoBg);
        setEnhanced(enhancedNoBg);
        setBgRemoved(true);
        return;
      }
      // Apply to current preview (which may be cropped/enhanced already)
      const cleaned = await removeBackground(preview, { aggressiveness: 1.1 });
      setPreviewWithBg(preview);
      setEnhancedNoBg(cleaned);
      setPreview(cleaned);
      setEnhanced(cleaned);
      setBgRemoved(true);
      toast.success("تمت إزالة الخلفية");
    } catch (err) {
      console.warn("Background removal failed:", err);
      toast.error("تعذرت إزالة الخلفية");
    } finally {
      setBgRemoving(false);
    }
  };

  const zoomIn = () => setZoom((z) => Math.min(MAX_ZOOM, +(z + ZOOM_STEP).toFixed(2)));
  const zoomOut = () => setZoom((z) => Math.max(MIN_ZOOM, +(z - ZOOM_STEP).toFixed(2)));
  const zoomReset = () => setZoom(1);

  // ----- Pinch-to-zoom (mobile) -----
  const getTouchDistance = (touches) => {
    const [a, b] = touches;
    const dx = a.clientX - b.clientX;
    const dy = a.clientY - b.clientY;
    return Math.hypot(dx, dy);
  };

  const handleTouchStart = (e) => {
    if (e.touches.length === 2) {
      pinchStateRef.current = {
        startDistance: getTouchDistance(e.touches),
        startZoom: zoom,
        active: true,
      };
    }
  };

  const handleTouchMove = (e) => {
    const st = pinchStateRef.current;
    if (st.active && e.touches.length === 2) {
      e.preventDefault();
      const dist = getTouchDistance(e.touches);
      if (st.startDistance > 0) {
        const factor = dist / st.startDistance;
        const next = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, +(st.startZoom * factor).toFixed(2)));
        setZoom(next);
      }
    }
  };

  const handleTouchEnd = (e) => {
    if (e.touches.length < 2) {
      pinchStateRef.current.active = false;
    }
  };

  // Double-tap to toggle 1× / 2×
  const handleTap = () => {
    const now = Date.now();
    if (now - lastTapRef.current < 280) {
      setZoom((z) => (z === 1 ? 2 : 1));
    }
    lastTapRef.current = now;
  };

  return (
    <div className="space-y-8 fade-up max-w-3xl mx-auto" data-testid="add-invoice-page">
      <div>
        <div className="text-xs uppercase tracking-wider text-[#8A8D86] mb-2 font-readex">إضافة فاتورة</div>
        <h1 className="font-readex font-semibold text-3xl sm:text-4xl text-[#1A1C19] leading-tight">
          التقاط أو رفع فاتورة
        </h1>
        <p className="text-[#5C605A] mt-2">جودة عالية + كشف تلقائي للحدود + تكبير دقيق قبل التحليل.</p>
      </div>

      {!preview ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <input
            ref={cameraRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            data-testid="camera-input"
            onChange={(e) => handleFile(e.target.files?.[0])}
          />
          <input
            ref={fileRef}
            type="file"
            accept="image/*,application/pdf"
            className="hidden"
            data-testid="file-input"
            onChange={(e) => handleFile(e.target.files?.[0])}
          />

          <button
            type="button"
            onClick={() => cameraRef.current?.click()}
            data-testid="camera-btn"
            className="bg-white border border-[#1A1C19]/15 rounded-md p-8 flex flex-col items-center gap-3 hover:border-[#1F3A33] hover:bg-[#F0EFEB] transition-all shadow-3d-sm hover:shadow-3d card-3d"
          >
            <div className="w-12 h-12 rounded-md bg-[#1F3A33] text-white flex items-center justify-center shadow-3d-emerald">
              <Camera className="w-6 h-6" strokeWidth={1.5} />
            </div>
            <div className="text-center">
              <div className="font-readex font-medium text-lg text-[#1A1C19]">التقاط بالكاميرا</div>
              <div className="text-xs text-[#8A8D86] mt-1">جودة 2200px + شحذ تلقائي</div>
            </div>
          </button>

          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            data-testid="upload-btn"
            className="bg-white border border-[#1A1C19]/15 rounded-md p-8 flex flex-col items-center gap-3 hover:border-[#1F3A33] hover:bg-[#F0EFEB] transition-all shadow-3d-sm hover:shadow-3d card-3d"
          >
            <div className="w-12 h-12 rounded-md bg-[#1F3A33] text-white flex items-center justify-center shadow-3d-emerald">
              <ImagePlus className="w-6 h-6" strokeWidth={1.5} />
            </div>
            <div className="text-center">
              <div className="font-readex font-medium text-lg text-[#1A1C19]">رفع من المعرض</div>
              <div className="text-xs text-[#8A8D86] mt-1">صورة أو ملف PDF</div>
            </div>
          </button>

          {/* Feature badges */}
          <div className="sm:col-span-2 flex flex-wrap gap-2 justify-center">
            <Badge icon={Sparkles}>كشف تلقائي للحدود</Badge>
            <Badge icon={FileUp}>رفع PDF</Badge>
            <Badge icon={Crop}>اقتصاص حر</Badge>
            <Badge icon={ZoomIn}>OCR بـ GPT-4o</Badge>
          </div>

          <div className="sm:col-span-2 mt-4">
            <button
              type="button"
              onClick={manualEntry}
              data-testid="manual-entry-btn"
              className="w-full bg-transparent border border-[#1A1C19]/20 text-[#1A1C19] hover:border-[#1A1C19] hover:bg-black/5 transition-colors rounded-md px-6 py-3.5 font-medium font-readex inline-flex items-center justify-center gap-2"
            >
              <Pen className="w-4 h-4" strokeWidth={1.5} />
              إدخال يدوي بدون صورة
            </button>
          </div>
        </div>
      ) : overlayMode && detectedBounds ? (
        // ---------- Overlay (scanner) mode — shows detected bounds in teal ----------
        <div className="bg-white border border-[#1A1C19]/10 rounded-md overflow-hidden shadow-3d-sm" data-testid="overlay-mode">
          <div className="flex items-center justify-between gap-3 px-4 py-2.5 bg-[#1A1C19] text-white">
            <div className="flex items-center gap-2 text-xs font-readex">
              <Sparkles className="w-3.5 h-3.5 text-[#22D3A0]" strokeWidth={1.75} />
              <span>تم اكتشاف حدود الفاتورة</span>
            </div>
            <button onClick={reset} className="p-1 -me-1" aria-label="إلغاء">
              <X className="w-4 h-4 text-white/80" strokeWidth={1.75} />
            </button>
          </div>

          <div className="bg-[#0F1614] flex items-center justify-center p-3 sm:p-6">
            <ReceiptDetectionOverlay src={originalPreview} bounds={detectedBounds} />
          </div>

          {/* Bottom action bar — mimics the screenshot's pill buttons */}
          <div className="p-4 sm:p-5">
            <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3">
              <button
                onClick={confirmDetection}
                data-testid="confirm-detection-btn"
                className="bg-[#22D3A0] text-[#0F1614] hover:bg-[#1FB88A] active:translate-y-[1px] transition-all rounded-full px-6 py-3 font-medium font-readex inline-flex items-center justify-center gap-2 shadow-[0_4px_16px_rgba(34,211,160,0.3)]"
              >
                <Check className="w-5 h-5" strokeWidth={2} />
                تأكيد القص
              </button>
              <button
                onClick={() => setShowCrop(true)}
                data-testid="open-crop-btn-overlay"
                className="bg-[#1A1C19] text-white hover:bg-[#000] active:translate-y-[1px] transition-all rounded-full px-5 py-3 font-medium font-readex inline-flex items-center justify-center gap-2"
              >
                <Crop className="w-4 h-4" strokeWidth={1.75} />
                ضبط يدوي
              </button>
              <button
                onClick={skipDetection}
                data-testid="skip-detection-btn"
                className="bg-transparent border border-[#1A1C19]/20 text-[#1A1C19] hover:border-[#1A1C19] hover:bg-black/5 transition-colors rounded-full px-5 py-3 font-medium font-readex"
              >
                استخدام الأصل
              </button>
            </div>
            <p className="text-center text-xs text-[#8A8D86] mt-3 font-readex">
              اضبط الحدود قبل تحليل الفاتورة بـ GPT-4o.
            </p>
          </div>
        </div>
      ) : (
        <div className="bg-white border border-[#1A1C19]/10 rounded-md overflow-hidden shadow-3d-sm">
          {/* Status strip */}
          <div className="flex items-center justify-between gap-3 px-4 py-2.5 bg-[#F5F4EF] border-b border-[#1A1C19]/8">
            <div className="flex items-center gap-2 text-xs">
              {autoCropping ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin text-[#1F3A33]" strokeWidth={1.75} />
                  <span className="text-[#5C605A] font-readex">جارٍ كشف حدود الفاتورة…</span>
                </>
              ) : autoCropped ? (
                <>
                  <Sparkles className="w-3.5 h-3.5 text-[#6A8A76]" strokeWidth={1.75} />
                  <span className="text-[#1F3A33] font-readex font-medium">تم القص التلقائي</span>
                  <button
                    onClick={undoAutoCrop}
                    data-testid="undo-autocrop-btn"
                    className="ms-2 inline-flex items-center gap-1 text-[#C84C31] hover:text-[#A63D26] text-xs font-readex"
                  >
                    <Undo2 className="w-3.5 h-3.5" strokeWidth={1.75} />
                    العودة للأصل
                  </button>
                </>
              ) : (
                <>
                  <span className="w-1.5 h-1.5 rounded-full bg-[#6A8A76]" />
                  <span className="text-[#5C605A] font-readex">جودة محسّنة 2200px</span>
                </>
              )}
            </div>

            {/* Zoom controls + Compare toggle */}
            <div className="flex items-center gap-1">
              <button
                onClick={toggleRemoveBackground}
                disabled={!preview || bgRemoving}
                data-testid="remove-bg-btn"
                aria-label="إزالة الخلفية"
                title="إزالة الخلفية"
                className={`h-7 inline-flex items-center gap-1 px-2 me-2 rounded-md text-xs font-readex font-medium transition ${
                  bgRemoved
                    ? "bg-[#6A8A76] text-white"
                    : "text-[#1A1C19] hover:bg-[#F0EFEB]"
                } disabled:opacity-40 disabled:cursor-not-allowed`}
              >
                {bgRemoving ? (
                  <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.75} />
                ) : (
                  <Eraser className="w-4 h-4" strokeWidth={1.75} />
                )}
                {bgRemoved ? "خلفية مزالة" : "إزالة الخلفية"}
              </button>
              <button
                onClick={() => setCompareMode((v) => !v)}
                disabled={!enhanced || !originalPreview}
                data-testid="compare-toggle-btn"
                aria-label="مقارنة قبل/بعد"
                className={`h-7 inline-flex items-center gap-1 px-2 me-2 rounded-md text-xs font-readex font-medium transition ${
                  compareMode
                    ? "bg-[#1F3A33] text-white"
                    : "text-[#1A1C19] hover:bg-[#F0EFEB]"
                } disabled:opacity-40 disabled:cursor-not-allowed`}
              >
                <SplitSquareHorizontal className="w-4 h-4" strokeWidth={1.75} />
                قبل / بعد
              </button>
              <button
                onClick={zoomOut}
                disabled={zoom <= MIN_ZOOM || compareMode}
                data-testid="zoom-out-btn"
                aria-label="تصغير"
                className="w-7 h-7 inline-flex items-center justify-center rounded-md text-[#1A1C19] hover:bg-[#F0EFEB] disabled:opacity-40 disabled:cursor-not-allowed transition"
              >
                <ZoomOut className="w-4 h-4" strokeWidth={1.75} />
              </button>
              <span className="text-xs font-mono-num text-[#5C605A] w-10 text-center" data-testid="zoom-level">
                {Math.round(zoom * 100)}%
              </span>
              <button
                onClick={zoomIn}
                disabled={zoom >= MAX_ZOOM || compareMode}
                data-testid="zoom-in-btn"
                aria-label="تكبير"
                className="w-7 h-7 inline-flex items-center justify-center rounded-md text-[#1A1C19] hover:bg-[#F0EFEB] disabled:opacity-40 disabled:cursor-not-allowed transition"
              >
                <ZoomIn className="w-4 h-4" strokeWidth={1.75} />
              </button>
              <button
                onClick={zoomReset}
                disabled={zoom === 1 || compareMode}
                data-testid="zoom-reset-btn"
                aria-label="إعادة"
                className="w-7 h-7 inline-flex items-center justify-center rounded-md text-[#1A1C19] hover:bg-[#F0EFEB] disabled:opacity-40 disabled:cursor-not-allowed transition"
              >
                <Maximize2 className="w-4 h-4" strokeWidth={1.75} />
              </button>
            </div>
          </div>

          {/* Image preview area — either zoom mode or compare mode */}
          {compareMode && enhanced && originalPreview ? (
            <div className="relative">
              <BeforeAfterSlider before={originalPreview} after={enhanced} height="60vh" />
              <button
                onClick={reset}
                data-testid="remove-image-btn"
                className="absolute top-2 end-2 z-10 w-9 h-9 rounded-md bg-white/95 hover:bg-white text-[#1A1C19] flex items-center justify-center shadow"
                aria-label="إزالة الصورة"
              >
                <X className="w-5 h-5" strokeWidth={1.5} />
              </button>
            </div>
          ) : (
            <div className="relative bg-[#F0EFEB]">
              <div
                className="overflow-auto max-h-[60vh] flex items-center justify-center p-2 touch-none"
                data-testid="image-preview-area"
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                onClick={handleTap}
              >
                <img
                  src={preview}
                  alt="معاينة الفاتورة"
                  className="select-none transition-transform duration-200 origin-top"
                  style={{ transform: `scale(${zoom})`, maxWidth: zoom === 1 ? "100%" : "none" }}
                  draggable={false}
                />
              </div>
              <button
                onClick={reset}
                data-testid="remove-image-btn"
                className="absolute top-2 end-2 w-9 h-9 rounded-md bg-white/95 hover:bg-white text-[#1A1C19] flex items-center justify-center shadow"
                aria-label="إزالة الصورة"
              >
                <X className="w-5 h-5" strokeWidth={1.5} />
              </button>
            </div>
          )}

          <div className="p-5 sm:p-6 flex flex-col sm:flex-row gap-3">
            <button
              type="button"
              onClick={analyze}
              disabled={analyzing || autoCropping}
              data-testid="analyze-btn"
              className="flex-1 bg-[#1F3A33] text-white hover:bg-[#172A25] active:translate-y-[1px] disabled:opacity-60 transition-all rounded-md px-6 py-3.5 font-medium font-readex inline-flex items-center justify-center gap-2 shadow-[0_2px_8px_rgba(31,58,51,0.18)] hover:shadow-[0_8px_20px_rgba(31,58,51,0.28)]"
            >
              {analyzing ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" strokeWidth={1.5} />
                  جاري التحليل…
                </>
              ) : (
                <>
                  <FileUp className="w-5 h-5" strokeWidth={1.5} />
                  تحليل الفاتورة
                </>
              )}
            </button>
            <button
              type="button"
              onClick={() => setShowCrop(true)}
              data-testid="open-crop-btn"
              className="bg-white border border-[#1A1C19]/20 text-[#1A1C19] hover:border-[#1F3A33] hover:bg-[#F0EFEB] transition-colors rounded-md px-6 py-3.5 font-medium font-readex inline-flex items-center justify-center gap-2"
            >
              <Crop className="w-5 h-5" strokeWidth={1.5} />
              اقتصاص يدوي
            </button>
            <button
              type="button"
              onClick={manualEntry}
              data-testid="manual-with-image-btn"
              className="bg-transparent border border-[#1A1C19]/20 text-[#1A1C19] hover:border-[#1A1C19] hover:bg-black/5 transition-colors rounded-md px-6 py-3.5 font-medium font-readex"
            >
              إدخال يدوي
            </button>
          </div>
        </div>
      )}

      {showCrop && preview && (
        <CropModal
          image={preview}
          onConfirm={applyCrop}
          onCancel={() => setShowCrop(false)}
        />
      )}
    </div>
  );
}

function Badge({ icon: Icon, children }) {
  return (
    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-[#1A1C19]/10 text-xs text-[#5C605A] font-readex">
      <Icon className="w-3.5 h-3.5 text-[#1F3A33]" strokeWidth={1.75} />
      {children}
    </span>
  );
}
